/**
 * Die Felder von „Zähler anlegen" und „Zähler bearbeiten" (Spec 46a, „Blatt")
 * — ein Formular, zwei Modi. Testid-Präfix `meter-create-*` bzw. `meter-edit-*`.
 *
 * Zwei Gruppen: die Hauptfelder (Art, Zählernummer, Zweck) und die „weiteren
 * Angaben" (Stellenzahl, Tarif, Messstellenbetreiber, Ablesefrist). Beim
 * Anlegen klappt die zweite hinter `<details>`, beim Bearbeiten steht sie offen.
 * Art, Einheit und Tarife kommen aus `GET /energy/meter-options`; die Art ist
 * nach dem Anlegen unveränderlich und erscheint beim Bearbeiten als Lesezeile.
 */

import type { Ref } from 'react'

import { EnumSelectField } from '@/components/EnumSelectField'
import { FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import type { MeterOptions } from '@/features/energy/api'
import { DIGIT_OPTIONS, digitsOnly } from '@/features/energy/meter-form'
import type { MeterFormValues } from '@/features/energy/meter-form'

type Prefix = 'meter-create' | 'meter-edit'

interface FieldsProps {
  readonly prefix: Prefix
  readonly form: MeterFormValues
  readonly onChange: (name: keyof MeterFormValues) => (value: string) => void
  readonly error: FieldError | undefined
}

interface PrimaryFieldsProps extends FieldsProps {
  /** Beim Anlegen die Auswahl, beim Bearbeiten `undefined` (dann `kindText`). */
  readonly options: MeterOptions | undefined
  readonly isOptionsPending: boolean
  /** Beim Bearbeiten: „Strom · kWh" als Lesezeile. */
  readonly kindText?: string
  readonly serialRef?: Ref<HTMLInputElement>
}

export function MeterPrimaryFields({
  prefix,
  form,
  onChange,
  error,
  options,
  isOptionsPending,
  kindText,
  serialRef,
}: PrimaryFieldsProps) {
  const selectedKind = options?.kinds.find((kind) => kind.value === form.kind)

  return (
    <>
      {kindText === undefined ? (
        <div className="flex flex-col gap-1">
          <EnumSelectField
            label="Art"
            testId={`${prefix}-kind`}
            apiField="kind"
            errorPrefix={prefix}
            options={options?.kinds ?? []}
            isPending={isOptionsPending}
            placeholder="Art wählen"
            required
            value={form.kind}
            onChange={onChange('kind')}
            error={error}
          />
          {selectedKind !== undefined && (
            <p data-testid={`${prefix}-unit`} className="text-label text-muted">
              Einheit {selectedKind.unit}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="text-label text-muted">Art</span>
          <p data-testid={`${prefix}-kind`} className="text-body text-text min-h-(--tap-min) py-2">
            {kindText}
          </p>
        </div>
      )}

      <FormField
        {...(serialRef === undefined ? {} : { ref: serialRef })}
        label="Zählernummer"
        testId={`${prefix}-serial`}
        apiField="serial"
        errorPrefix={prefix}
        autoCapitalize="characters"
        required
        value={form.serial}
        onChange={onChange('serial')}
        error={error}
      />
      <FormField
        label="Zweck"
        testId={`${prefix}-purpose`}
        apiField="purpose"
        errorPrefix={prefix}
        placeholder="z. B. Wärmepumpe"
        value={form.purpose}
        onChange={onChange('purpose')}
        error={error}
      />
    </>
  )
}

interface MoreFieldsProps extends FieldsProps {
  readonly tariffs: MeterOptions['tariffs']
  readonly isOptionsPending: boolean
}

export function MeterMoreFields({ prefix, form, onChange, error, tariffs, isOptionsPending }: MoreFieldsProps) {
  const setIntervalText = onChange('interval')

  return (
    <>
      <EnumSelectField
        label="Stellenzahl"
        testId={`${prefix}-digits`}
        apiField="digits"
        errorPrefix={prefix}
        options={DIGIT_OPTIONS}
        isPending={false}
        value={form.digits}
        onChange={onChange('digits')}
        error={error}
      />
      <EnumSelectField
        label="Tarif"
        testId={`${prefix}-tariff`}
        apiField="tariff"
        errorPrefix={prefix}
        options={tariffs}
        isPending={isOptionsPending}
        value={form.tariff}
        onChange={onChange('tariff')}
        error={error}
      />
      <FormField
        label="Messstellenbetreiber"
        testId={`${prefix}-operator`}
        apiField="operator"
        errorPrefix={prefix}
        value={form.operator}
        onChange={onChange('operator')}
        error={error}
      />
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <FormField
            label="Ablesefrist"
            testId={`${prefix}-interval`}
            apiField="reading_interval_days"
            errorPrefix={prefix}
            inputMode="numeric"
            value={form.interval}
            onChange={(value) => {
              setIntervalText(digitsOnly(value))
            }}
            error={error}
          />
        </div>
        <span className="text-label text-muted flex min-h-(--tap-min) items-center">Tage</span>
      </div>
    </>
  )
}
